import { api } from "@/lib/apiClient";

export interface BankOption {
  id: number;
  name: string;
  shortName: string | null;
  code: string | null;
  bankType: string | null;
  sortOrder: number;
  chartOfAccountsId: number | null;
}

type BankApiRecord = {
  id: number | string;
  name: string;
  short_name?: string | null;
  code?: string | null;
  bank_type?: string | null;
  sort_order?: number | string | null;
  chart_of_accounts_id?: number | string | null;
};

function extractRows<T>(body: unknown): T[] {
  if (Array.isArray(body)) return body as T[];
  if (body && typeof body === "object") {
    const data = (body as Record<string, unknown>).data;
    if (Array.isArray(data)) return data as T[];
  }
  return [];
}

let banksCache: BankOption[] | null = null;
let banksPromise: Promise<BankOption[]> | null = null;

export async function fetchBanks(force = false): Promise<BankOption[]> {
  if (!force && banksCache) return banksCache;
  if (!force && banksPromise) return banksPromise;

  banksPromise = (async () => {
    const response = await api.get("/api/v1/banks/options");
    const rows = extractRows<BankApiRecord>(response.data);
    banksCache = rows.map((row) => ({
      id: Number(row.id),
      name: String(row.name ?? ""),
      shortName: row.short_name != null ? String(row.short_name) : null,
      code: row.code != null ? String(row.code) : null,
      bankType: row.bank_type != null ? String(row.bank_type) : null,
      sortOrder: Number(row.sort_order ?? 0),
      chartOfAccountsId: row.chart_of_accounts_id != null ? Number(row.chart_of_accounts_id) : null,
    }));
    return banksCache;
  })();

  try {
    return await banksPromise;
  } finally {
    banksPromise = null;
  }
}

export function filterBanks(banks: BankOption[], query: string): BankOption[] {
  const q = query.trim().toLowerCase();
  if (!q) return banks;
  return banks.filter((bank) => {
    const haystack = [bank.name, bank.shortName ?? "", bank.code ?? ""].join(" ").toLowerCase();
    return haystack.includes(q);
  });
}

export function bankOptionLabel(bank: BankOption): string {
  return bank.code ? `${bank.name} (${bank.code})` : bank.name;
}
